// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { MessageModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NotificationItem, {
    NotificationItemPresentationProps,
} from "@store/Library/Components/Notifications/NotificationItem";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import { css } from "styled-components";

export interface NotificationListPresentationProps extends ExpandStyles<NotificationListStyles> {}

export interface NotificationListProps extends NotificationListPresentationProps {
    /** Notifications to render, already filtered/sorted by the caller. */
    messages: MessageModel[];
    /** Session language, forwarded to each item for localized relative time. */
    language?: string;
    onToggleRead: (message: MessageModel) => void;
    /** Called when an action link is clicked (before navigation) — used to close the flyout/panel. */
    onActionClick?: () => void;
    /** Page type appended to each action link as `returnPage` (see NotificationItem). */
    actionReturnPageType?: string;
    /** Empty-state copy for the current tab. Defaults to the shared "No messages" string. */
    emptyStateText?: string;
}

export interface NotificationListStyles {
    wrapper?: InjectableCss;
    emptyStateWrapper?: InjectableCss;
    emptyStateText?: TypographyPresentationProps;
    notificationItem?: NotificationItemPresentationProps;
}

export const notificationListStyles: NotificationListStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    emptyStateWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: ${pxToRem(32, 23)};
        `,
    },
    emptyStateText: {
        css: css`
            color: ${getColor("text.light")};
        `,
    },
};

/**
 * Presentational, reusable list of notifications shared by the desktop flyout and the mobile
 * panel. Uses flex + per-item padding with full-bleed dividers (no container padding), so separators
 * span edge to edge.
 */
const NotificationList: FC<NotificationListProps> = ({
    messages,
    language,
    onToggleRead,
    onActionClick,
    actionReturnPageType,
    emptyStateText,
    extendedStyles,
    overridedStyles,
}) => {
    const styles = useExpandProps(notificationListStyles, extendedStyles, overridedStyles);

    if (messages.length === 0) {
        return (
            <StyledWrapper {...styles.emptyStateWrapper} data-test-selector="notifications_emptyState">
                <Typography {...styles.emptyStateText}>{emptyStateText ?? translate("No messages")}</Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="notifications_list">
            {messages.map(message => (
                <NotificationItem
                    key={message.id}
                    message={message}
                    language={language}
                    onToggleRead={onToggleRead}
                    onActionClick={onActionClick}
                    actionReturnPageType={actionReturnPageType}
                    {...styles.notificationItem}
                />
            ))}
        </StyledWrapper>
    );
};

export default NotificationList;
